import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users2, Hash, Building2 } from "lucide-react";

export const NewConversationDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const { startDm, createChannel, startPartner } = useMessaging();
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);
  const [channelName, setChannelName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/team").then(({ data }) => setTeam(data.filter((m) => m.user_id !== user.user_id))).catch(() => {});
    api.get("/partnerships").then(({ data }) => setPartners(data.filter((p) => p.status === "accepted"))).catch(() => {});
  }, [open, user.user_id]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="new-conversation-dialog">
        <DialogHeader><DialogTitle className="font-heading">New message</DialogTitle></DialogHeader>
        <Tabs defaultValue="dm">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dm" data-testid="tab-dm"><Users2 className="mr-1.5 h-4 w-4" /> Teammate</TabsTrigger>
            <TabsTrigger value="channel" data-testid="tab-channel"><Hash className="mr-1.5 h-4 w-4" /> Channel</TabsTrigger>
            <TabsTrigger value="partner" data-testid="tab-partner"><Building2 className="mr-1.5 h-4 w-4" /> Partner</TabsTrigger>
          </TabsList>

          <TabsContent value="dm" className="mt-3 max-h-72 space-y-1 overflow-y-auto">
            {team.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">No teammates yet.</p>}
            {team.map((m) => (
              <button key={m.user_id} disabled={busy} onClick={() => run(() => startDm(m.user_id))}
                data-testid={`dm-target-${m.user_id}`}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">
                <span className="font-medium">{m.name}</span>
                <span className="text-xs text-muted-foreground">{m.role}</span>
              </button>
            ))}
          </TabsContent>

          <TabsContent value="channel" className="mt-3 space-y-3">
            <p className="text-xs text-muted-foreground">Create a team channel everyone in your agency can join.</p>
            <div className="flex gap-2">
              <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} placeholder="e.g. new-leads" data-testid="channel-name-input" />
              <Button disabled={busy || !channelName.trim()} onClick={() => run(() => createChannel(channelName.trim()))} data-testid="create-channel-button">Create</Button>
            </div>
          </TabsContent>

          <TabsContent value="partner" className="mt-3 max-h-72 space-y-1 overflow-y-auto">
            {partners.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">No connected partner agencies. Connect one in the Directory.</p>}
            {partners.map((p) => (
              <button key={p.other_org_id} disabled={busy} onClick={() => run(() => startPartner(p.other_org_id))}
                data-testid={`partner-target-${p.other_org_id}`}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">
                <Building2 className="h-4 w-4 text-amber-500" /> <span className="font-medium">{p.other_org_name}</span>
              </button>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
